import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { RefreshCw, X, ArrowLeft } from 'lucide-react';
import { formatDate } from '../utils/dateFormat';
import { fetchJSON } from '../utils/api';

// QR / URL training check-in (docs/modules/ld-selfservice.md). The QR at the
// venue encodes /checkin/<token>; scanning lands here (after login), one tap
// records attendance. Identity = login email vs staff master.
//
// GPS: the page asks the phone for its
// position on load, shows the reading (and, when the session has a venue with
// coordinates, the distance and an in/out-of-range answer), and files the raw
// reading with the attendance row. Location is never a gate — checking in with
// no GPS, or from outside the venue, is allowed after an explicit confirm.
// The verdict stored on the row is computed SERVER-side from the submitted
// reading; what this page shows is a live preview of the same formula.
// Client-side copy of server/utils/geo.js — live preview only; the stored
// verdict is always the server's own computation.
const distanceMeters = (lat1, lng1, lat2, lng2) => {
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(a));
};

// The three ways a reading fails need three different instructions — "please
// enable" means something different for a denied permission (browser/site
// settings), a device that cannot position at all, and a fix that timed out.
const LOC_FAIL_TEXT = {
  denied: 'Location permission was denied. Enable location for this site in your browser settings, then press Re-detect.',
  unavailable: 'Your device could not determine a position. Turn on GPS/location services and press Re-detect.',
  timeout: 'Getting a position took too long. Move somewhere with better reception and press Re-detect.',
  unsupported: 'This browser cannot provide a location here (location needs a secure HTTPS connection).',
};

const CheckinPage = () => {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);
  const [acting, setActing] = useState(false);
  // What the successful POST said about this scan: { inRange, distanceM } | null.
  const [resultGeo, setResultGeo] = useState(null);

  // ── Location state ──────────────────────────────────────────────────────
  // status: detecting | ok | denied | unavailable | timeout | unsupported
  const [locStatus, setLocStatus] = useState('detecting');
  const [loc, setLoc] = useState(null); // { latitude, longitude, accuracyM }
  // Which confirm dialog is open before submitting: 'nogps' | 'outside' | null.
  const [confirming, setConfirming] = useState(null);
  const detectSeq = useRef(0);

  const detect = useCallback(() => {
    if (!navigator.geolocation) {
      setLocStatus('unsupported');
      return;
    }
    const seq = ++detectSeq.current;
    setLocStatus('detecting');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        if (seq !== detectSeq.current) return;
        setLoc({
          latitude: Math.round(pos.coords.latitude * 1e6) / 1e6,
          longitude: Math.round(pos.coords.longitude * 1e6) / 1e6,
          accuracyM: pos.coords.accuracy != null ? Math.round(pos.coords.accuracy * 10) / 10 : null,
        });
        setLocStatus('ok');
      },
      (err) => {
        if (seq !== detectSeq.current) return;
        setLoc(null);
        setLocStatus(err.code === 1 ? 'denied' : (err.code === 3 ? 'timeout' : 'unavailable'));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  }, []);

  const load = useCallback(async () => {
    try {
      const data = await fetchJSON(`/api/ld/checkin/${token}`);
      setInfo(data);
      setAlready(data.alreadyCheckedIn);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { detect(); }, [detect]);

  const session = info?.session;
  const venue = session?.venue || null;
  // Measurable = the session names a venue that carries a point and a radius.
  // Sessions without one still capture the reading — there is just no verdict.
  const measurable = !!(venue && venue.latitude != null && venue.radiusM != null);
  const distance = measurable && loc
    ? Math.round(distanceMeters(loc.latitude, loc.longitude, venue.latitude, venue.longitude))
    : null;
  const inRange = distance != null ? distance <= venue.radiusM : null;

  const submit = async () => {
    setConfirming(null);
    setActing(true);
    setError(null);
    try {
      const body = loc
        ? { latitude: loc.latitude, longitude: loc.longitude, accuracyM: loc.accuracyM }
        : {};
      const data = await fetchJSON(`/api/ld/checkin/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      setDone(true);
      setAlready(data.alreadyCheckedIn);
      setResultGeo(data.geo || null);
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  // The button's own gatekeeping: no reading → ask once;
  // reading says outside the venue → say so and ask; in range (or no venue to
  // measure against) → just check in.
  const onCheckinClick = () => {
    if (!loc) { setConfirming('nogps'); return; }
    if (inRange === false) { setConfirming('outside'); return; }
    submit();
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-4">
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 w-full max-w-md text-center">
        <h1 className="text-lg font-bold text-slate-900">Training Check-In</h1>

        {!info && !error && <p className="mt-4 text-sm text-slate-500">Loading…</p>}

        {session && (
          <div className="mt-4 border border-slate-200 rounded-lg p-4 bg-slate-50 text-left">
            <p className="text-sm font-semibold text-slate-900">{session.courseTitle}</p>
            <p className="text-xs text-slate-500 mt-1">
              {formatDate(session.date)}
              {session.startEndTime && (
                <><span className="mx-1.5 text-slate-300">·</span>{session.startEndTime}</>
              )}
            </p>
            <p className="text-xs text-slate-500 mt-1">{session.hours} training hour{session.hours === 1 ? '' : 's'}</p>
            {venue?.name && (
              <p className="text-xs text-slate-500 mt-1">{venue.name}</p>
            )}
          </div>
        )}

        {/* ── Location block ─────────────────────────────────────────────── */}
        {info && info.staffMatched && !info.trainer && !done && !already && (
          <div className="mt-4 border border-slate-200 rounded-lg p-4 text-left">
            <div className="flex items-center justify-between gap-2">
              <p className="text-xs font-semibold text-slate-500 uppercase">Your Location</p>
              <button
                type="button"
                onClick={detect}
                disabled={locStatus === 'detecting'}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${locStatus === 'detecting' ? 'animate-spin' : ''}`} />
                {locStatus === 'detecting' ? 'Detecting…' : 'Re-detect'}
              </button>
            </div>

            {locStatus === 'ok' && loc && (
              <div className="mt-2 space-y-1.5">
                <p className="text-sm text-slate-700 tabular-nums">
                  {loc.latitude}, {loc.longitude}
                  {loc.accuracyM != null && (
                    <span className="text-xs text-slate-600"> (±{Math.round(loc.accuracyM)} m)</span>
                  )}
                </p>
                {measurable ? (
                  inRange ? (
                    <p className="inline-block px-2 py-1 rounded-full bg-green-50 text-green-700 text-xs font-medium">
                      At the venue — about {distance} m from {venue.name} (allowed {venue.radiusM} m)
                    </p>
                  ) : (
                    <p className="inline-block px-2 py-1 rounded-full bg-amber-50 text-amber-800 text-xs font-medium">
                      Outside the venue — about {distance} m from {venue.name} (allowed {venue.radiusM} m)
                    </p>
                  )
                ) : (
                  <p className="text-xs text-slate-500">
                    This session has no venue coordinates, so there is nothing to measure against —
                    your location is still recorded.
                  </p>
                )}
              </div>
            )}

            {locStatus === 'detecting' && (
              <p className="mt-2 text-sm text-slate-500">Getting your position…</p>
            )}

            {['denied', 'unavailable', 'timeout', 'unsupported'].includes(locStatus) && (
              <div className="mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs">
                {LOC_FAIL_TEXT[locStatus]} You can still check in without a location.
              </div>
            )}

            <p className="mt-2.5 text-[11px] text-slate-500">
              Your location will be recorded with this check-in.
            </p>
          </div>
        )}

        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm text-left">
            {error}
          </div>
        )}

        {info && !info.staffMatched && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm text-left">
            Your account email does not match an active staff record, so attendance cannot
            be recorded. Ask an administrator to update the staff master.
          </div>
        )}

        {info && info.staffMatched && !info.trainer && !session?.checkinOpen && !done && !already && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-sm">
            Check-in for this session is currently closed.
          </div>
        )}

        {/* The trainer has nothing to check in to: their
            hours are booked automatically when HR marks the session completed,
            so the button is replaced by the explanation. */}
        {info?.trainer ? (
          <div className="mt-5 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 text-sm text-left">
            You are a trainer for this session — there is nothing to check in to. Your trainer
            hours are added automatically once the session is marked completed.
          </div>
        ) : (done || already) ? (
          <div className="mt-5 space-y-2">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {already && !done
                ? 'You are already checked in for this session.'
                : 'Attendance recorded — you are checked in!'}
            </div>
            {/* What this scan's reading said — stated once, on the receipt. */}
            {done && !already && (
              resultGeo ? (
                resultGeo.inRange === true ? (
                  <p className="text-xs text-green-700">
                    Location verified — about {Math.round(resultGeo.distanceM)} m from the venue.
                  </p>
                ) : resultGeo.inRange === false ? (
                  <p className="text-xs text-amber-700">
                    Recorded as outside the venue — about {Math.round(resultGeo.distanceM)} m away.
                  </p>
                ) : (
                  <p className="text-xs text-slate-500">
                    Location recorded. This session has no venue coordinates to compare against.
                  </p>
                )
              ) : (
                <p className="text-xs text-slate-500">Checked in without a location.</p>
              )
            )}
          </div>
        ) : (
          info && info.staffMatched && session?.checkinOpen && (
            <button
              onClick={onCheckinClick}
              disabled={acting}
              className="mt-5 w-full py-3 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {acting ? 'Checking In…' : 'Check In Now'}
            </button>
          )
        )}

        <Link to="/" className="mt-6 inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </Link>
      </div>

      {/* One extra question, only when the reading argues with the check-in:
          no reading at all, or a reading outside the
          venue. A clean in-range reading submits straight through. */}
      {confirming && (
        <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full">
            <div className="flex items-center justify-between p-5 border-b border-slate-200">
              <h3 className="text-base font-semibold text-slate-900">
                {confirming === 'nogps' ? 'Check In Without a Location?' : 'You Appear to Be Outside the Venue'}
              </h3>
              <button onClick={() => setConfirming(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-3 text-left">
              {confirming === 'nogps' ? (
                <p className="text-sm text-slate-700">
                  No location has been detected, so this check-in will be recorded without one.
                  You can press <span className="font-medium">Re-detect</span> first, or check in anyway.
                </p>
              ) : (
                <p className="text-sm text-slate-700">
                  Your position reads about <span className="font-medium">{distance} m</span> from{' '}
                  <span className="font-medium">{venue?.name}</span> (allowed {venue?.radiusM} m).
                  The check-in will be recorded as outside the venue.
                </p>
              )}
              <div className="flex justify-end gap-2 pt-1">
                <button
                  onClick={() => setConfirming(null)}
                  className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded"
                >
                  Cancel
                </button>
                <button
                  onClick={submit}
                  disabled={acting}
                  className="px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 disabled:opacity-50"
                >
                  Check In Anyway
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CheckinPage;
